<template>
  <div class="transit" :class="{ 'transit--active': active }" aria-hidden="true">
    <div class="transit__plates">
      <div
          v-for="(url, index) in plates"
          :key="url"
          class="transit__plate"
          :class="{ 'transit__plate--shown': index === plateIndex }"
          :style="{ backgroundImage: `url('${url}')` }"
      ></div>
    </div>

    <div class="transit__fog transit__fog--near"></div>
    <div class="transit__fog transit__fog--far"></div>
    <div class="transit__vignette"></div>

    <div class="transit__caption" role="status" aria-live="polite">
      <p class="transit__title">{{ t('transit.title') }}</p>
      <div class="transit__phrase-slot">
        <transition name="transit-phrase" mode="out-in">
          <p :key="phrase" class="transit__phrase">{{ phrase }}</p>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onBeforeUnmount, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import transitionService from '@/services/transitionService.js';

const props = defineProps({
  active: {type: Boolean, default: false},
  positionId: {type: String, default: ''},
});

const {t, tm} = useI18n();

/* Long enough to finish reading a two-line phrase without hurrying, and to let
 * a plate settle rather than flicking past. */
const PLATE_MS = 20000;
const PHRASE_MS = 15000;
const CATEGORIES = ['lore', 'rumor', 'humor', 'road'];

const plates = ref(transitionService.defaults);
const plateIndex = ref(0);
const phrase = ref('');

let plateTimer = null;
let phraseTimer = null;

/*
 * tm() hands back raw messages — plain strings here, but compiled functions
 * whenever a line carries interpolation. Resolve those through t() rather than
 * rendering "[object Object]", the same way RiftLoader does.
 */
function readCategory(name) {
  const lines = tm(`transit.${name}`);
  if (!Array.isArray(lines)) return [];
  return lines.map((line, i) =>
      typeof line === 'string' ? line : t(`transit.${name}[${i}]`)
  );
}

function nextPhrase() {
  const pool = CATEGORIES.flatMap(readCategory);
  if (!pool.length) return;
  let candidate = phrase.value;
  /* Two draws is enough to avoid an immediate repeat without looping on a
   * single-entry pool. */
  for (let attempt = 0; attempt < 2 && candidate === phrase.value; attempt += 1) {
    candidate = pool[Math.floor(Math.random() * pool.length)];
  }
  phrase.value = candidate;
}

function nextPlate() {
  if (plates.value.length < 2) return;
  plateIndex.value = (plateIndex.value + 1) % plates.value.length;
}

async function start() {
  const urls = await transitionService.getImages(props.positionId);
  /* The overlay may have closed while the request was in flight. */
  if (!props.active) return;

  plates.value = urls;
  plateIndex.value = Math.floor(Math.random() * urls.length);
  nextPhrase();

  stopTimers();
  plateTimer = setInterval(nextPlate, PLATE_MS);
  phraseTimer = setInterval(nextPhrase, PHRASE_MS);
}

function stopTimers() {
  if (plateTimer) clearInterval(plateTimer);
  if (phraseTimer) clearInterval(phraseTimer);
  plateTimer = null;
  phraseTimer = null;
}

watch(
    () => props.active,
    (isActive) => {
      if (isActive) {
        start();
      } else {
        stopTimers();
      }
    },
    {immediate: true}
);

onBeforeUnmount(stopTimers);
</script>

<style scoped>
.transit {
  position: absolute;
  inset: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  transition: opacity 1.6s ease-in-out;
}

.transit--active {
  opacity: 1;
}

.transit__plates,
.transit__plate {
  position: absolute;
  inset: 0;
}

.transit__plate {
  background-size: cover;
  background-position: center;
  opacity: 0;
  transform: scale(1.06);
  transition: opacity 2.6s ease-in-out, transform 12s linear;
}

.transit__plate--shown {
  opacity: 1;
  transform: scale(1);
}

/* Two haze banks drifting at different rates read as depth without a canvas —
   the game loop keeps the main thread, this stays on the compositor. */
.transit__fog {
  position: absolute;
  inset: -25%;
  background-repeat: no-repeat;
  will-change: transform;
}

.transit__fog--near {
  background-image:
      radial-gradient(38% 46% at 22% 62%, rgba(24, 78, 90, 0.55), transparent 70%),
      radial-gradient(30% 38% at 74% 48%, rgba(18, 46, 78, 0.5), transparent 72%);
  animation: transit-drift-near 46s ease-in-out infinite alternate;
}

.transit__fog--far {
  background-image:
      radial-gradient(52% 40% at 58% 78%, rgba(12, 34, 52, 0.6), transparent 74%),
      radial-gradient(26% 30% at 34% 26%, rgba(60, 48, 96, 0.35), transparent 76%);
  animation: transit-drift-far 71s ease-in-out infinite alternate;
}

.transit__vignette {
  position: absolute;
  inset: 0;
  background: radial-gradient(
      68% 68% at 50% 52%,
      transparent 30%,
      rgba(4, 10, 16, 0.72) 100%
  );
}

@keyframes transit-drift-near {
  from {
    transform: translate3d(-4%, 1%, 0) scale(1.05);
  }
  to {
    transform: translate3d(5%, -2%, 0) scale(1.14);
  }
}

@keyframes transit-drift-far {
  from {
    transform: translate3d(3%, -1%, 0) scale(1.12);
  }
  to {
    transform: translate3d(-5%, 2%, 0) scale(1.02);
  }
}

/* Sits above the action bar, which occupies roughly the lower third of the
   background. Any lower and the phrase is unreadable behind the panel. */
.transit__caption {
  position: absolute;
  left: 50%;
  bottom: 38%;
  transform: translateX(-50%);
  width: min(62ch, 82%);
  text-align: center;
  text-wrap: balance;
}

/*
 * The plates run from near-black to a bright cyan reflection, so the copy needs
 * a scrim — but a scrim painted on the caption box shows its own straight edges
 * and reads as a panel dropped onto the scene. This one lives on a pseudo
 * element far larger than the text and fades out well inside its own bounds, so
 * the darkening has no boundary to see.
 */
.transit__caption::before {
  content: "";
  position: absolute;
  inset: -220% -60%;
  background: radial-gradient(
      closest-side ellipse at 50% 50%,
      rgba(3, 9, 15, 0.85) 0%,
      rgba(3, 9, 15, 0.62) 34%,
      rgba(3, 9, 15, 0.28) 58%,
      transparent 78%
  );
  pointer-events: none;
}

.transit__title {
  position: relative;
}

.transit__title {
  margin: 0 0 0.6em;
  font-size: 0.72rem;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: rgba(126, 220, 232, 0.72);
}

/*
 * The caption is anchored to its bottom edge, so a phrase that wraps to a
 * second line used to push the whole block — title included — upward, and the
 * heading jumped on every change. The slot reserves the tallest case up front
 * and the lines fill it from the top, so nothing above it ever moves.
 */
.transit__phrase-slot {
  position: relative;
  font-size: clamp(0.95rem, 1.5vw, 1.2rem);
  min-height: calc(3 * 1.55em);
}

.transit__phrase {
  margin: 0;
  font-size: inherit;
  line-height: 1.55;
  color: rgba(226, 240, 244, 0.94);
  text-shadow: 0 2px 18px rgba(2, 8, 14, 0.95);
}

.transit-phrase-enter-active,
.transit-phrase-leave-active {
  transition: opacity 0.9s ease, transform 0.9s ease;
}

.transit-phrase-enter-from {
  opacity: 0;
  transform: translateY(0.5em);
}

.transit-phrase-leave-to {
  opacity: 0;
  transform: translateY(-0.5em);
}

@media (prefers-reduced-motion: reduce) {
  .transit__fog--near,
  .transit__fog--far {
    animation: none;
  }

  .transit__plate {
    transition: opacity 0.4s linear;
    transform: none;
  }

  .transit-phrase-enter-active,
  .transit-phrase-leave-active {
    transition: opacity 0.2s linear;
  }
}
</style>
